import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/AlarmProfilesPage.jsx
import React, { useState, useMemo } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Button,
    Alert,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tabs,
    Tab,
    Paper,
    IconButton,
    Tooltip,
    Menu,
    MenuItem as MenuItemComponent,
    Divider,
    Chip
} from '@mui/material';

import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ContentCopy as DuplicateIcon,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    ViewModule as GridViewIcon,
    TableRows as TableViewIcon,
    ManageAccounts as ManageIcon,
    LinkOff as UnlinkIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    PersonAdd as AssignIcon
} from '@mui/icons-material';

import { useAuth } from '../hooks/useAuth';
import { useApiCall, useMutation } from '../hooks/useApi';
import { api, profileUtils } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';
import LoadingPage from '../components/common/LoadingPage';

// Importar componentes separados
import ProfileForm from '../components/AlarmProfiles/ProfileForm';
import ProfileCard from '../components/AlarmProfiles/ProfileCard';
import ProfileTable from '../components/AlarmProfiles/ProfileTable';
import ProfileDetail from '../components/AlarmProfiles/ProfileDetail';
import AssignmentDialog from '../components/AlarmProfiles/AssignmentDialog';
import AssignmentsTable from '../components/AlarmProfiles/AssignmentsTable';

const AlarmProfilesPage = () => {
  const { t } = useTranslation();
    const { hasPermission } = useAuth();
    const { showSuccess, showError } = useNotification();

    const buildDefaultMetrics = () => ({
        PSI: profileUtils.normalizeMetricConfig('PSI', {
            enabled: true,
            threshold_min: 90,
            threshold_max: 135,
            ideal_value: 120,
            unit: 'PSI'
        }),
        TEMP: profileUtils.normalizeMetricConfig('TEMP', {
            enabled: true,
            threshold_min: null,
            threshold_max: 75,
            ideal_value: 65,
            unit: 'C'
        })
    });

    const [tabValue, setTabValue] = useState(0);
    const [selectedProfile, setSelectedProfile] = useState(null);

    // Dialogs
    const [profileFormOpen, setProfileFormOpen] = useState(false);
    const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [profileDetailOpen, setProfileDetailOpen] = useState(false);
    const [profileDetailProfile, setProfileDetailProfile] = useState(null);
    const [confirmDeleteAssignmentOpen, setConfirmDeleteAssignmentOpen] = useState(false);

    const [newProfileName, setNewProfileName] = useState('');
    const [profileToDuplicate, setProfileToDuplicate] = useState(null);
    const [profileToDelete, setProfileToDelete] = useState(null);
    const [assignmentToDelete, setAssignmentToDelete] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [assignmentSearchTerm, setAssignmentSearchTerm] = useState('');
    const [mainViewMode, setMainViewMode] = useState('grid');
    const [anchorEl, setAnchorEl] = useState(null);

    const [selectedVehicles, setSelectedVehicles] = useState([]);
    const [compatibleVehicles, setCompatibleVehicles] = useState([]);
    const [loadingCompatibility, setLoadingCompatibility] = useState(false);

    // Form state
    const [profileForm, setProfileForm] = useState({
        name: '',
        description: '',
        axle_count: 3,
        applies_to_all_axles: true,
        configurations: []
    });

    const [tpmsSettings, setTpmsSettings] = useState(profileUtils.getDefaultTPMSSettings());

    const [globalMetrics, setGlobalMetrics] = useState(buildDefaultMetrics);

    const [axleMetrics, setAxleMetrics] = useState({});

    // API calls
    const {
        data: profiles,
        loading: profilesLoading,
        error: profilesError,
        refetch: refetchProfiles
    } = useApiCall(() => {
        const params = {};
        if (tabValue === 0) {
            params.active_only = true;
            params.include_templates = false;
        } else if (tabValue === 1) {
            params.active_only = false;
            params.include_templates = false;
        } else if (tabValue === 2) {
            params.active_only = true;
            params.include_templates = true;
        }
        return api.alarmProfiles.getAll(params);
    }, [tabValue]);

    const {
        data: assignments,
        loading: assignmentsLoading,
        refetch: refetchAssignments
    } = useApiCall(() => api.alarmProfiles.getAssignments(), []);

    const { data: vehicles, loading: vehiclesLoading, refetch: refetchVehicles } = useApiCall(
        () => api.vehicles.getAll()
    );

    // Mutations
    const { mutate: deleteProfile, loading: deleting } = useMutation(
        (id) => api.alarmProfiles.delete(id),
        {
            onSuccess: () => {
                showSuccess(t('hardcoded.pages_alarmprofilespage.perfil_eliminado_exitosamente'));
                refetchProfiles();
                setConfirmDeleteOpen(false);
                setProfileToDelete(null);
                setProfileDetailOpen(false);
                setProfileDetailProfile(null);
            },
            onError: (error) => showError(error.message || t('hardcoded.pages_alarmprofilespage.error_al_eliminar_perfil'))
        }
    );

    const { mutate: deleteAssignment, loading: deletingAssignment } = useMutation(
        (assignmentId) => api.alarmProfiles.removeAssignment(assignmentId),
        {
            onSuccess: () => {
                showSuccess(t('hardcoded.pages_alarmprofilespage.asignacion_eliminada_exitosamente'));
                refetchAssignments();
                setConfirmDeleteAssignmentOpen(false);
                setAssignmentToDelete(null);
            },
            onError: (error) => showError(error.message || t('hardcoded.pages_alarmprofilespage.error_al_eliminar_asignacion'))
        }
    );

    const { mutate: duplicateProfile, loading: duplicating } = useMutation(
        async ({ profileId, newName }) => {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:8010'
                }/alarm-profiles/${profileId}/duplicate?new_name=${encodeURIComponent(newName)}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: t('hardcoded.pages_alarmprofilespage.bearer_localstorageget', { localStorageget: localStorage.getItem('authToken') || localStorage.getItem('access_token') })
                    }
                }
            );
            if (!response.ok) throw new Error(`Error ${response.status}`);
            return response.json();
        },
        {
            onSuccess: (data) => {
                showSuccess(t('hardcoded.pages_alarmprofilespage.perfil_name_creado_exitosamente', { name: data?.name }));
                refetchProfiles();
                setDuplicateDialogOpen(false);
                setNewProfileName('');
                setProfileToDuplicate(null);
            },
            onError: (error) => showError(error.message || t('hardcoded.pages_alarmprofilespage.error_al_duplicar_perfil'))
        }
    );

    const { mutate: createProfile, loading: creating } = useMutation(
        (data) => api.alarmProfiles.create(data),
        {
            onSuccess: (data) => {
                showSuccess(t('hardcoded.pages_alarmprofilespage.perfil_name_creado_exitosamente', { name: data.data?.name }));
                refetchProfiles();
                setProfileFormOpen(false);
                resetForm();
            },
            onError: (error) => showError(error.message || t('hardcoded.pages_alarmprofilespage.error_al_crear_perfil'))
        }
    );

    const { mutate: updateProfile, loading: updating } = useMutation(
        async ({ id, data }) => api.alarmProfiles.update(id, data),
        {
            onSuccess: (resp) => {
                const name = resp?.data?.name || resp?.name || profileForm.name;
                showSuccess(t('hardcoded.pages_alarmprofilespage.perfil_name_actualizado_exitosamente', { name: name }));
                refetchProfiles();
                refetchAssignments();
                setProfileFormOpen(false);
                setProfileDetailProfile((prev) =>
                    prev ? { ...prev, ...profileForm, tpms_settings: tpmsSettings, configurations: generateConfigurations() } : prev
                );
            },
            onError: (error) => showError(error.message || t('hardcoded.pages_alarmprofilespage.error_al_actualizar_perfil'))
        }
    );

    const { mutate: assignProfile, loading: assigning } = useMutation(
        (data) => api.alarmProfiles.assignToVehicle(data),
        {
            onSuccess: () => {
                showSuccess(t('hardcoded.pages_alarmprofilespage.perfil_asignado_exitosamente'));
                refetchAssignments();
            },
            onError: (error) => {
                let errorMessage = t('hardcoded.components_vehicles_detail_alarmprofilestab.error_al_asignar_perfil');
                if (error.response?.data?.detail) {
                    if (typeof error.response.data.detail === 'string') {
                        errorMessage = error.response.data.detail;
                    } else if (Array.isArray(error.response.data.detail)) {
                        errorMessage = error.response.data.detail.map((d) => d.msg || d).join(', ');
                    }
                }
                showError(errorMessage);
            }
        }
    );

    // Compatibility
    const loadCompatibleVehicles = async (profile) => {
        if (!profile || !vehicles) {
            setCompatibleVehicles([]);
            return;
        }
        setLoadingCompatibility(true);
        try {
            const compatible = vehicles.filter((vehicle) => vehicle.axle_count === profile.axle_count);
            setCompatibleVehicles(compatible);
        } catch (error) {
            console.error(t('hardcoded.pages_alarmprofilespage.error_loading_compatible_vehicles'), error);
            setCompatibleVehicles([]);
        } finally {
            setLoadingCompatibility(false);
        }
    };

    // Handlers
    const handleTabChange = (event, newValue) => setTabValue(newValue);

    const handleCreateProfile = () => {
        setSelectedProfile(null);
        resetForm();
        setProfileFormOpen(true);
    };

    const handleOpenProfileDetail = (profile) => {
        if (!profile) return;
        const fromList =
            profiles?.find((p) => String(p.id) === String(profile.id)) ||
            profiles?.find((p) => String(p.id) === String(profile.profile_id)) ||
            profile;
        setProfileDetailProfile(fromList);
        setProfileDetailOpen(true);
    };

    const handleCloseProfileDetail = () => {
        setProfileDetailOpen(false);
        setProfileDetailProfile(null);
    };

    const handleEditFromDetail = () => {
        if (!profileDetailProfile) return;
        handleEditProfile(profileDetailProfile);
        setProfileDetailOpen(false);
    };

    const handleEditProfile = (profile) => {
        setSelectedProfile(profile);

        setProfileForm({
            name: profile.name,
            description: profile.description || '',
            axle_count: profile.axle_count,
            applies_to_all_axles: profile.applies_to_all_axles,
            configurations: profile.configurations || []
        });
        setTpmsSettings(profile.tpms_settings || profileUtils.getDefaultTPMSSettings());

        if (profile.applies_to_all_axles) {
            const metrics = buildDefaultMetrics();
            metrics.PSI.enabled = false;
            metrics.TEMP.enabled = false;

            (profile.configurations || []).forEach((config) => {
                if (Number(config.axle_number) === 1) {
                    metrics[config.metric_type] = profileUtils.normalizeMetricConfig(config.metric_type, config);
                }
            });

            setGlobalMetrics(metrics);
            setAxleMetrics({});
        } else {
            const axleConfigs = {};
            (profile.configurations || []).forEach((config) => {
                const key = `${config.axle_number}-${config.metric_type}`;
                axleConfigs[key] = profileUtils.normalizeMetricConfig(config.metric_type, config);
            });

            setAxleMetrics(axleConfigs);
            setGlobalMetrics(buildDefaultMetrics());
        }

        setProfileFormOpen(true);
        handleMenuClose();
    };

    const handleDeleteProfile = (profile) => {
        setProfileToDelete(profile);
        setConfirmDeleteOpen(true);
        handleMenuClose();
    };

    const handleDeleteAssignment = (assignment) => {
        setAssignmentToDelete(assignment);
        setConfirmDeleteAssignmentOpen(true);
    };

    const handleDuplicateProfile = (profile) => {
        setProfileToDuplicate(profile);
        setNewProfileName(t('hardcoded.pages_alarmprofilespage.name_-_copia', { name: profile.name }));
        setDuplicateDialogOpen(true);
        handleMenuClose();
    };

    const handleAssignProfile = async (profile) => {
        handleMenuClose();
        setSelectedProfile(profile);
        setSelectedVehicles([]);
        if (!vehicles && !vehiclesLoading) {
            await refetchVehicles();
        }
        await loadCompatibleVehicles(profile);
        setAssignmentDialogOpen(true);
    };

    const handleMenuOpen = (event, profile) => {
        setAnchorEl(event.currentTarget);
        setSelectedProfile(profile);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const resetForm = () => {
        setProfileForm({
            name: '',
            description: '',
            axle_count: 3,
            applies_to_all_axles: true,
            configurations: []
        });
        setTpmsSettings(profileUtils.getDefaultTPMSSettings());
        setGlobalMetrics(buildDefaultMetrics());
        setAxleMetrics({});
    };

    const handleFormChange = (field, value) => {
        setProfileForm((prev) => ({ ...prev, [field]: value }));

        if (field === 'axle_count') setAxleMetrics({});
        if (field === 'applies_to_all_axles' && value) setAxleMetrics({});
    };

    const handleGlobalMetricChange = (metricType, field, value) => {
        setGlobalMetrics((prev) => ({
            ...prev,
            [metricType]: {
                ...profileUtils.normalizeMetricConfig(metricType, prev[metricType]),
                [field]: value
            }
        }));
    };

    const handleAxleMetricChange = (axleNumber, metricType, field, value) => {
        const key = `${axleNumber}-${metricType}`;
        setAxleMetrics((prev) => ({
            ...prev,
            [key]: {
                ...profileUtils.normalizeMetricConfig(metricType, prev[key] || {}),
                [field]: value
            }
        }));
    };

    const handleTpmsSettingChange = (path, value) => {
        setTpmsSettings((prev) => {
            const next = JSON.parse(JSON.stringify(prev || profileUtils.getDefaultTPMSSettings()));
            let pointer = next;

            for (let index = 0; index < path.length - 1; index += 1) {
                const key = path[index];
                if (!pointer[key] || typeof pointer[key] !== 'object') pointer[key] = {};
                pointer = pointer[key];
            }

            pointer[path[path.length - 1]] = value;
            return next;
        });
    };

    const generateConfigurations = () => {
        return profileUtils.generateConfigurations(profileForm, globalMetrics, axleMetrics).map((config) => {
            const advanced = config.advanced_settings || profileUtils.getDefaultAdvancedSettings(config.metric_type, config, tpmsSettings);

            // Derivación automática de threshold_min/max para PSI a partir del ideal_value
            // y los % de la severidad "alarm" (Alerta Atención). Esto mantiene compatibilidad
            // con la validación legacy del backend y el resto de servicios que aún leen
            // threshold_min/max, sin exponer esos campos en la UI.
            if (config.metric_type === 'PSI') {
                const ideal = Number(config.ideal_value);
                const alarm = advanced?.pressure?.severity_levels?.alarm || {};
                const lowPct = Number(alarm.low);
                const highPct = Number(alarm.high);
                if (Number.isFinite(ideal) && ideal > 0) {
                    if (Number.isFinite(lowPct)) {
                        config = { ...config, threshold_min: Number((ideal * (1 + lowPct / 100)).toFixed(2)) };
                    }
                    if (Number.isFinite(highPct)) {
                        config = { ...config, threshold_max: Number((ideal * (1 + highPct / 100)).toFixed(2)) };
                    }
                }
            }

            return { ...config, advanced_settings: advanced };
        });
    };

    const previewConfigurations = generateConfigurations().map((config) => ({
        ...config,
        preview: profileUtils.buildPreview(config.metric_type, config, tpmsSettings)
    }));

    const handleSubmitProfile = () => {
        if (!profileForm.name.trim()) {
            showError(t('hardcoded.pages_alarmprofilespage.el_nombre_del_perfil_es'));
            return;
        }

        const configurations = generateConfigurations();

        if (configurations.length === 0) {
            showError(t('hardcoded.pages_alarmprofilespage.debe_configurar_al_menos_una'));
            return;
        }

        const validationErrors = configurations.flatMap((config) => {
            const result = profileUtils.validateMetricConfig(config);
            return result.isValid ? [] : result.errors.map((error) => t('hardcoded.pages_alarmprofilespage.metric_type_eje_axle_number_error', { metric_type: config.metric_type, axle_number: config.axle_number, error: error }));
        });

        if (validationErrors.length > 0) {
            showError(validationErrors[0]);
            return;
        }

        const profileData = { ...profileForm, tpms_settings: tpmsSettings, configurations };

        if (selectedProfile?.id) {
            updateProfile({ id: selectedProfile.id, data: profileData });
            return;
        }

        createProfile(profileData);
    };

    const handleAssignToVehicles = async () => {
        if (!selectedProfile || !selectedProfile.id) {
            showError(t('hardcoded.pages_alarmprofilespage.error_no_se_ha_seleccionado'));
            return;
        }

        if (selectedVehicles.length === 0) {
            showError(t('hardcoded.pages_alarmprofilespage.debe_seleccionar_al_menos_un'));
            return;
        }

        try {
            const payload = selectedVehicles.map((vehicleId) => ({
                vehicle_id: vehicleId,
                profile_id: selectedProfile.id,
                active: true
            }));

            const results = await Promise.allSettled(
                payload.map(
                    (assignment) => new Promise((resolve, reject) => {
                        assignProfile(assignment, {
                            onSuccess: resolve,
                            onError: reject
                        });
                    })
                )
            );

            const successCount = results.filter((result) => result.status === 'fulfilled').length;
            const errorCount = results.filter((result) => result.status === 'rejected').length;

            if (successCount > 0) {
                showSuccess(t('hardcoded.pages_alarmprofilespage.asignado_a_successcount_vehiculos', { successCount: successCount }));
                setAssignmentDialogOpen(false);
                setSelectedVehicles([]);
                setCompatibleVehicles([]);
                await refetchAssignments();
            }

            if (errorCount > 0) {
                showError(t('hardcoded.pages_alarmprofilespage.error_al_asignar_errorcount_vehiculos', { errorCount: errorCount }));
            }
        } catch (error) {
            showError(t('hardcoded.pages_alarmprofilespage.error_al_asignar_perfil_message', { message: error.message }));
        }
    };

    // Utility
    const getProfileAssignments = (profileId) => {
        if (!assignments || !profileId) return [];
        return assignments.filter((a) => String(a.profile_id) === String(profileId) && a.active);
    };

    const getVehicleDisplayName = (vehicle) => {
        if (!vehicle) return t('hardcoded.components_alarms_alarmlist.vehiculo_desconocido');
        return vehicle.alias || vehicle.name || `${vehicle.make} ${vehicle.model}`.trim() || t('hardcoded.pages_alarmprofilespage.sin_nombre');
    };

    const getFilteredAssignments = () => {
        if (!assignments) return [];
        let filtered = assignments;
        if (assignmentSearchTerm.trim()) {
            const search = assignmentSearchTerm.toLowerCase().trim();
            filtered = filtered.filter(
                (assignment) =>
                    assignment.vehicle_name?.toLowerCase().includes(search) ||
                    assignment.profile_name?.toLowerCase().includes(search) ||
                    assignment.profile?.name?.toLowerCase().includes(search)
            );
        }
        return filtered;
    };

    const getFilteredProfiles = () => {
        if (!profiles) return [];
        let filtered = [];
        if (tabValue === 0) filtered = profiles.filter((p) => p.active && !p.is_template);
        else if (tabValue === 1) filtered = profiles.filter((p) => !p.is_template);
        else if (tabValue === 2) filtered = profiles.filter((p) => p.is_template);

        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase().trim();
            filtered = filtered.filter(
                (profile) =>
                    profile.name.toLowerCase().includes(search) ||
                    (profile.description && profile.description.toLowerCase().includes(search)) ||
                    profile.metrics_configured?.some((metric) => metric.toLowerCase().includes(search))
            );
        }
        return filtered;
    };

    const currentProfiles = useMemo(getFilteredProfiles, [profiles, tabValue, searchTerm]);
    const currentAssignments = useMemo(getFilteredAssignments, [assignments, assignmentSearchTerm]);

    // Guards
    if (profilesLoading && !profiles) return <LoadingPage message={t('hardcoded.pages_alarmprofilespage.cargando_perfiles_de_alarma')} />;

    if (profilesError && !profiles) {
        return (
            <Container maxWidth={false} disableGutters>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <WarningIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        {t('hardcoded.pages_alarmprofilespage.error_al_cargar_perfiles')}</Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        {profilesError.message || t('hardcoded.pages_alarmprofilespage.error_inesperado')}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => window.location.reload()}
                        startIcon={<RefreshIcon />}
                    >
                        {i18n.t('hardcoded.components_ai_airecommendationspanel.reintentar')}</Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth={false} disableGutters>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 4,
                    flexWrap: 'wrap',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                        {t('hardcoded.pages_alarmprofilespage.perfiles_de_alerta')}</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder={tabValue === 3 ? t('hardcoded.pages_alarmprofilespage.buscar_asignaciones') : t('hardcoded.pages_alarmprofilespage.buscar_perfiles')}
                        value={tabValue === 3 ? assignmentSearchTerm : searchTerm}
                        onChange={(e) =>
                            tabValue === 3
                                ? setAssignmentSearchTerm(e.target.value)
                                : setSearchTerm(e.target.value)
                        }
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            endAdornment: (tabValue === 3 ? assignmentSearchTerm : searchTerm) && (
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        tabValue === 3 ? setAssignmentSearchTerm('') : setSearchTerm('')
                                    }
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )
                        }}
                        sx={{ width: 250 }}
                    />

                    {tabValue !== 3 && (
                        <Box sx={{ display: 'flex', bgcolor: 'grey.100', borderRadius: 1, p: 0.5 }}>
                            <Tooltip title={t('vehicles.viewModes.grid')}>
                                <IconButton
                                    size="small"
                                    onClick={() => setMainViewMode('grid')}
                                    sx={{
                                        bgcolor: mainViewMode === 'grid' ? 'white' : 'transparent',
                                        boxShadow: mainViewMode === 'grid' ? 1 : 0
                                    }}
                                >
                                    <GridViewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t('vehicles.viewModes.table')}>
                                <IconButton
                                    size="small"
                                    onClick={() => setMainViewMode('table')}
                                    sx={{
                                        bgcolor: mainViewMode === 'table' ? 'white' : 'transparent',
                                        boxShadow: mainViewMode === 'table' ? 1 : 0
                                    }}
                                >
                                    <TableViewIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    <Tooltip title={t('audit.actions.UPDATE')}>
                        <IconButton
                            onClick={() => {
                                refetchProfiles();
                                refetchAssignments();
                            }}
                            disabled={profilesLoading || assignmentsLoading}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>

                    {hasPermission('manager') && tabValue !== 3 && (
                        <Button data-tutorial-id="add-profile-btn" variant="contained" startIcon={<AddIcon />} onClick={handleCreateProfile}>
                            {t('hardcoded.components_alarmprofiles_profileform.crear_perfil')}</Button>
                    )}
                </Box>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ '& .MuiTabs-scrollButtons': { '&.Mui-disabled': { opacity: 0.3 } } }}
                >
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {i18n.t('hardcoded.pages_alarmprofilespage.perfiles_activos')}<Chip
                                    label={
                                        profiles ? profiles.filter((p) => p.active && !p.is_template).length : 0
                                    }
                                    size="small"
                                    color="primary"
                                />
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}<Chip
                                    label={profiles ? profiles.filter((p) => !p.is_template).length : 0}
                                    size="small"
                                    variant="outlined"
                                />
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {i18n.t('hardcoded.pages_alarmprofilespage.plantillas')}<Chip
                                    label={profiles ? profiles.filter((p) => p.is_template).length : 0}
                                    size="small"
                                    color="secondary"
                                />
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ManageIcon fontSize="small" />
                                {t('hardcoded.pages_alarmprofilespage.asignaciones')}<Chip
                                    label={assignments ? assignments.length : 0}
                                    size="small"
                                    color="info"
                                />
                            </Box>
                        }
                    />
                </Tabs>
            </Paper>

            {/* Content */}
            <Box>
                {tabValue !== 3 && searchTerm && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2">
                                {currentProfiles.length === 0
                                    ? t('hardcoded.pages_alarmprofilespage.no_se_encontraron_perfiles_para', { searchTerm: searchTerm })
                                    : t('hardcoded.pages_alarmprofilespage.se_encontraron_length_perfiles_para', { length: currentProfiles.length, searchTerm: searchTerm })}
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => setSearchTerm('')}
                                startIcon={<ClearIcon />}
                                sx={{ ml: 'auto' }}
                            >
                                {i18n.t('common.clear')}</Button>
                        </Alert>
                    </Box>
                )}

                {tabValue === 3 ? (
                    <AssignmentsTable
                        assignments={currentAssignments}
                        loading={assignmentsLoading}
                        profiles={profiles}
                        searchTerm={assignmentSearchTerm}
                        onClearSearch={() => setAssignmentSearchTerm('')}
                        onViewProfile={handleOpenProfileDetail}
                        onEditProfile={handleEditProfile}
                        onDeleteAssignment={handleDeleteAssignment}
                        hasPermission={hasPermission}
                        deletingAssignment={deletingAssignment}
                    />
                ) : currentProfiles.length === 0 ? (
                    <Paper sx={{ p: 6, textAlign: 'center' }}>
                        <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            {searchTerm ? t('hardcoded.pages_alarmprofilespage.no_se_encontraron_perfiles') : t('hardcoded.pages_alarmprofilespage.no_hay_perfiles')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            {searchTerm
                                ? `No hay perfiles que coincidan con "${searchTerm}"`
                                : tabValue === 2
                                    ? t('hardcoded.pages_alarmprofilespage.no_hay_plantillas_del_sistema')
                                    : t('hardcoded.pages_alarmprofilespage.crea_tu_primer_perfil_de')}
                        </Typography>
                        {searchTerm ? (
                            <Button
                                variant="outlined"
                                onClick={() => setSearchTerm('')}
                                startIcon={<ClearIcon />}
                                sx={{ mt: 2 }}
                            >
                                {i18n.t('common.clear')}</Button>
                        ) : (
                            hasPermission('manager') &&
                            tabValue !== 2 && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreateProfile}
                                    sx={{ mt: 2 }}
                                >
                                    {t('hardcoded.components_alarmprofiles_profileform.crear_perfil')}</Button>
                            )
                        )}
                    </Paper>
                ) : mainViewMode === 'table' ? (
                    <ProfileTable
                        profiles={currentProfiles}
                        onEdit={handleEditProfile}
                        onAssign={handleAssignProfile}
                        onDuplicate={handleDuplicateProfile}
                        onMenuOpen={handleMenuOpen}
                        onViewDetail={handleOpenProfileDetail}
                        hasPermission={hasPermission}
                        getProfileAssignments={getProfileAssignments}
                    />
                ) : (
                    <Grid container spacing={2}>
                        {currentProfiles.map((profile) => (
                            <Grid item xs={12} sm={6} md={4} key={profile.id}>
                                <ProfileCard
                                    profile={profile}
                                    onEdit={handleEditProfile}
                                    onAssign={handleAssignProfile}
                                    onDuplicate={handleDuplicateProfile}
                                    onMenuOpen={handleMenuOpen}
                                    onViewDetail={handleOpenProfileDetail}
                                    hasPermission={hasPermission}
                                    getProfileAssignments={getProfileAssignments}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* Dialogs */}
            <ProfileForm
                open={profileFormOpen}
                onClose={() => setProfileFormOpen(false)}
                profileForm={profileForm}
                tpmsSettings={tpmsSettings}
                globalMetrics={globalMetrics}
                axleMetrics={axleMetrics}
                previewConfigurations={previewConfigurations}
                selectedProfile={selectedProfile}
                creating={creating}
                updating={updating}
                onFormChange={handleFormChange}
                onTpmsSettingChange={handleTpmsSettingChange}
                onGlobalMetricChange={handleGlobalMetricChange}
                onAxleMetricChange={handleAxleMetricChange}
                onSubmit={handleSubmitProfile}
            />

            <ProfileDetail
                open={profileDetailOpen}
                onClose={handleCloseProfileDetail}
                profile={profileDetailProfile}
                onEdit={handleEditFromDetail}
                onAssign={() => handleAssignProfile(profileDetailProfile)}
                hasPermission={hasPermission}
            />

            <AssignmentDialog
                open={assignmentDialogOpen}
                onClose={() => {
                    setAssignmentDialogOpen(false);
                    setSelectedVehicles([]);
                    setCompatibleVehicles([]);
                }}
                profile={selectedProfile}
                vehicles={vehicles}
                compatibleVehicles={compatibleVehicles}
                selectedVehicles={selectedVehicles}
                setSelectedVehicles={setSelectedVehicles}
                loading={loadingCompatibility || vehiclesLoading}
                onAssign={handleAssignToVehicles}
                assigning={assigning}
                getProfileAssignments={getProfileAssignments}
                getVehicleDisplayName={getVehicleDisplayName}
            />

            {/* Context Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItemComponent onClick={() => handleOpenProfileDetail(selectedProfile)}>
                    <VisibilityIcon sx={{ mr: 1 }} fontSize="small" /> {i18n.t('hardcoded.components_alarmprofiles_profilecard.ver_detalle')}</MenuItemComponent>

                <MenuItemComponent onClick={() => handleEditProfile(selectedProfile)}>
                    <EditIcon sx={{ mr: 1 }} fontSize="small" /> {i18n.t('common.edit')}</MenuItemComponent>

                <MenuItemComponent onClick={() => handleDuplicateProfile(selectedProfile)}>
                    <DuplicateIcon sx={{ mr: 1 }} fontSize="small" /> {i18n.t('hardcoded.pages_alarmprofilespage.duplicar')}</MenuItemComponent>

                {selectedProfile && !selectedProfile.is_template && (
                    <MenuItemComponent onClick={() => handleAssignProfile(selectedProfile)}>
                        <AssignIcon sx={{ mr: 1 }} fontSize="small" /> {i18n.t('common.assign')}</MenuItemComponent>
                )}

                <Divider />

                <MenuItemComponent
                    onClick={() => handleDeleteProfile(selectedProfile)}
                    sx={{ color: 'error.main' }}
                    disabled={deleting}
                >
                    <DeleteIcon sx={{ mr: 1 }} fontSize="small" /> {t('audit.actions.DELETE')}</MenuItemComponent>
            </Menu>

            {/* Delete Dialogs */}
            <Dialog
                open={confirmDeleteOpen}
                onClose={() => {
                    setConfirmDeleteOpen(false);
                    setProfileToDelete(null);
                }}
            >
                <DialogTitle>{t('hardcoded.pages_alarmprofilespage.confirmar_eliminacion_de_perfil')}</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {t('hardcoded.pages_alarmprofilespage.esta_seguro_de_que_desea')}<strong>"{profileToDelete?.name}"</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('hardcoded.pages_alarmprofilespage.esta_accion_no_se_puede')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setConfirmDeleteOpen(false);
                            setProfileToDelete(null);
                        }}
                    >
                        {t('common.cancel')}</Button>
                    <Button
                        onClick={() => {
                            if (profileToDelete) deleteProfile(profileToDelete.id);
                        }}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    >
                        {deleting ? t('common.deleting') : t('audit.actions.DELETE')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDeleteAssignmentOpen}
                onClose={() => {
                    setConfirmDeleteAssignmentOpen(false);
                    setAssignmentToDelete(null);
                }}
            >
                <DialogTitle>{t('hardcoded.pages_alarmprofilespage.confirmar_eliminacion_de_asignacion')}</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {t('hardcoded.pages_alarmprofilespage.esta_seguro_de_que_desea')}{' '}
                        <strong>"{assignmentToDelete?.profile_name}"</strong> {t('hardcoded.pages_alarmprofilespage.del_vehiculo')}{' '}
                        <strong>"{assignmentToDelete?.vehicle_name}"</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('hardcoded.pages_alarmprofilespage.esta_accion_no_se_puede')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setConfirmDeleteAssignmentOpen(false);
                            setAssignmentToDelete(null);
                        }}
                    >
                        {t('common.cancel')}</Button>
                    <Button
                        onClick={() => {
                            if (assignmentToDelete) deleteAssignment(assignmentToDelete.id);
                        }}
                        color="error"
                        variant="contained"
                        disabled={deletingAssignment}
                        startIcon={deletingAssignment ? <CircularProgress size={20} /> : <UnlinkIcon />}
                    >
                        {deletingAssignment ? t('common.deleting') : t('audit.actions.DELETE')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={duplicateDialogOpen}
                onClose={() => {
                    setDuplicateDialogOpen(false);
                    setNewProfileName('');
                    setProfileToDuplicate(null);
                }}
            >
                <DialogTitle>{t('hardcoded.pages_alarmprofilespage.duplicar_perfil')}</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>{t('hardcoded.pages_alarmprofilespage.ingrese_un_nombre_para_el')}</Typography>
                    {profileToDuplicate && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('hardcoded.pages_alarmprofilespage.perfil_original')}{profileToDuplicate.name}"
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        value={newProfileName}
                        onChange={(e) => setNewProfileName(e.target.value)}
                        placeholder={t('hardcoded.pages_alarmprofilespage.nombre_del_nuevo_perfil')}
                        sx={{ mt: 2 }}
                        autoFocus
                        error={!newProfileName.trim()}
                        helperText={!newProfileName.trim() ? t('createUser.validation.nameRequired') : ''}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDuplicateDialogOpen(false);
                            setNewProfileName('');
                            setProfileToDuplicate(null);
                        }}
                    >
                        {t('common.cancel')}</Button>
                    <Button
                        onClick={() => {
                            if (profileToDuplicate && newProfileName.trim())
                                duplicateProfile({
                                    profileId: profileToDuplicate.id,
                                    newName: newProfileName.trim()
                                });
                        }}
                        disabled={!newProfileName.trim() || duplicating}
                        startIcon={duplicating ? <CircularProgress size={20} /> : <DuplicateIcon />}
                        variant="contained"
                    >
                        {duplicating ? t('hardcoded.pages_alarmprofilespage.duplicando') : t('hardcoded.pages_alarmprofilespage.duplicar')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AlarmProfilesPage;